import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, TrendingDown, TrendingUp, Clock, Cpu, RotateCw, AlertOctagon, ShieldAlert, ShieldCheck } from "lucide-react";

/**
 * FFT operational KPI panel (spec §8).
 * Each metric has a target and a measured value → pass/fail glyph.
 */
const KPI_DEFS = [
  {
    key: "stagnation_detection_rate_pct",
    target_key: "stagnation_detection_rate_min_pct",
    label: "Stagnation Detection Rate",
    icon: ShieldCheck,
    unit: "%",
    higher_is_better: true,
    description: "FFT flags ≥ MEDIUM when Contradiction=HIGH.",
  },
  {
    key: "false_positive_rate_pct",
    target_key: "false_positive_rate_max_pct",
    label: "False Positive Rate",
    icon: ShieldAlert,
    unit: "%",
    higher_is_better: false,
    description: "FFT=HIGH on Contradiction=LOW deals.",
  },
  {
    key: "added_latency_p95_ms",
    target_key: "added_latency_p95_max_ms",
    label: "Added Latency (p95)",
    icon: Clock,
    unit: " ms",
    higher_is_better: false,
    description: "Overhead added by the FFT layer.",
  },
  {
    key: "cpu_overhead_pct_estimate",
    target_key: "cpu_overhead_max_pct",
    label: "CPU Overhead (est.)",
    icon: Cpu,
    unit: "%",
    higher_is_better: false,
    description: "Estimated CPU cost of FFT per analysis.",
  },
  {
    key: "profile_freshness_oldest_days",
    target_key: "profile_freshness_max_days",
    label: "Oldest Profile Age",
    icon: RotateCw,
    unit: " d",
    higher_is_better: false,
    description: "Freshness of the oldest reference profile.",
  },
  {
    key: "fft_error_rate_pct",
    target_key: "fft_error_rate_max_pct",
    label: "FFT Error Rate",
    icon: AlertOctagon,
    unit: "%",
    higher_is_better: false,
    description: "Analyses where FFT was skipped/errored.",
  },
];

function isMetricHealthy(measured, target, higher_is_better) {
  if (measured == null || target == null) return null;
  return higher_is_better ? measured >= target : measured <= target;
}

export default function FFTKpiPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/v1/fft/kpis");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="border border-gray-200 bg-white rounded-sm p-6" data-testid="fft-kpi-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-gray-500" strokeWidth={2} />
          <span className="overline text-gray-400">FFT Layer — Operational KPIs</span>
        </div>
        {data && (
          <span
            className={`text-xs font-mono px-2 py-0.5 rounded-sm ${
              data.env_kill_switch_enabled
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
            data-testid="fft-env-flag"
          >
            {data.env_kill_switch_enabled ? "ISALE_FFT_ENABLED=true" : "KILL SWITCH ACTIVE"}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1 mb-5 leading-relaxed">
        Live rollout metrics per INOVI FFT Integration Note §8. Auto-refreshes every 15 s.
      </p>

      {loading ? (
        <div className="text-sm text-gray-400">Loading KPIs…</div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {KPI_DEFS.map((k) => {
              const measured = data.measured?.[k.key];
              const target = data.targets?.[k.target_key];
              const healthy = isMetricHealthy(measured, target, k.higher_is_better);
              const Icon = k.icon;
              return (
                <div
                  key={k.key}
                  className="border border-gray-200 rounded-sm p-4"
                  style={
                    healthy === false
                      ? { borderLeftColor: "#DC2626", borderLeftWidth: 3 }
                      : healthy === true
                      ? { borderLeftColor: "#16A34A", borderLeftWidth: 3 }
                      : {}
                  }
                  data-testid={`kpi-${k.key}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-3 h-3 text-gray-500" strokeWidth={2} />
                    <span className="overline text-gray-400 text-[10px]">{k.label}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <span
                      className={`metric-value text-2xl ${
                        healthy === false ? "text-[#DC2626]" : "text-gray-900"
                      }`}
                    >
                      {measured != null ? measured.toFixed(measured < 1 && measured > 0 ? 2 : 1) : "—"}
                      <span className="text-xs text-gray-400">{k.unit}</span>
                    </span>
                    {healthy != null && (
                      healthy ? (
                        <TrendingUp className="w-3.5 h-3.5 text-[#16A34A]" strokeWidth={2} />
                      ) : (
                        <TrendingDown className="w-3.5 h-3.5 text-[#DC2626]" strokeWidth={2} />
                      )
                    )}
                  </div>
                  <div className="text-[10px] font-mono text-gray-400">
                    target: {k.higher_is_better ? "≥" : "≤"} {target}{k.unit}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-3 md:grid-cols-6 gap-3 text-xs">
            {Object.entries({
              "analyses": data.counters?.total_analyses,
              "fft_run": data.counters?.fft_run,
              "fft_err": data.counters?.fft_errors,
              "profiles": data.counters?.active_profiles,
              "expired": data.counters?.expired_profiles,
              "flagged": data.counters?.fft_flagged_on_high,
            }).map(([k, v]) => (
              <div key={k} className="flex flex-col">
                <span className="overline text-gray-400 text-[10px]">{k}</span>
                <span className="font-mono text-sm">{v ?? 0}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
